import { useEffect, useState, useCallback } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { useLazyQuery, useMutation, gql, useReactiveVar } from '@apollo/client'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'

import {
  ConfirmDialog,
  EditableComponent,
  DialogReorderTipsToolsItems,
  DialogEditTipsToolsItem,
} from '../components'
import { ROLE_MANAGER } from '../constants'
import { ReactComponent as ArrowRightIcon } from '../assets/icons/arrow-right-icon.svg'
import {
  apolloAuthData,
  apolloEditStatusData,
  getEditStatus,
  setEditStatus,
} from '../services/apollo'
import { CREATE_TIPSTOOLS_ITEM } from '../services/admin'
import { generateSafeLink, serializeFromQuery } from '../utils'

const GET_TIPSTOOLS_PAGES = gql`
  query TipsToolsPages {
    tipsToolsPages {
      edges {
        node {
          databaseId
          postId
          title
          overview
        }
      }
    }
  }
`
const GET_TIPSTOOLS_ITEMS = gql`
  query TipsToolsItems {
    tipsToolsItems(first: 1000) {
      edges {
        node {
          id
          databaseId
          order
          postId
          title
        }
      }
    }
  }
`
const GET_TIPSTOOLS_ITEMS_WITH_DETAIL = gql`
  query TipsToolsItems {
    tipsToolsItems(first: 1000) {
      edges {
        node {
          id
          databaseId
          order
          postId
          title
          detail {
            resources {
              id
              type
              name
              url
            }
          }
        }
      }
    }
  }
`
const TipsTools = () => {
  const auth = useReactiveVar(apolloAuthData)
  let location = useLocation()
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let [searchParams, setSearchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const [updatedItems, setUpdatedItems] = useState([])
  const [removingDatabaseId, setRemovingDatabaseId] = useState(-1)
  const editStatus = useReactiveVar(apolloEditStatusData)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [tipsToolsPage, setTipsToolsPage] = useState(null)
  const [tipsToolsItems, setTipsToolsItems] = useState([])
  const [getTipsToolsPages] = useLazyQuery(GET_TIPSTOOLS_PAGES, {
    fetchPolicy: 'no-cache',
  })
  const [getTipsToolsItems] = useLazyQuery(GET_TIPSTOOLS_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [getTipsToolsItemsWithDetail] = useLazyQuery(
    GET_TIPSTOOLS_ITEMS_WITH_DETAIL,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [createTipsToolsItem, { loading: creatingTipsToolsItem }] = useMutation(
    CREATE_TIPSTOOLS_ITEM
  )
  const items =
    updatedItems && updatedItems.length > 0
      ? updatedItems.sort((a, b) => a.order - b.order)
      : tipsToolsItems.sort((a, b) => a.order - b.order)

  const loadData = useCallback(async () => {
    setIsDataLoading(true)
    let tasks = [getTipsToolsPages()]

    if (isEditOn) {
      tasks.push(getTipsToolsItemsWithDetail())
    } else {
      tasks.push(getTipsToolsItems())
    }

    Promise.all(tasks)
      .then((responses) => {
        // Tips & Tools page
        const tipsToolsPages =
          responses[0]?.data?.tipsToolsPages?.edges?.map((edge) => edge.node) ??
          []
        const _tipsToolsPage = tipsToolsPages[0] ?? null
        setTipsToolsPage(_tipsToolsPage)

        // Tips & Tools items
        let tipsToolsItems =
          responses[1]?.data?.tipsToolsItems?.edges?.map((edge) => edge.node) ??
          []
        setTipsToolsItems(tipsToolsItems.sort((a, b) => a.order - b.order))
      })
      .catch((err) => {
        console.error('Unable to load data', err)
      })
      .finally(() => {
        setIsDataLoading(false)
      })
  }, [
    getTipsToolsPages,
    getTipsToolsItems,
    getTipsToolsItemsWithDetail,
    isEditOn,
  ])

  // Load data every time the edit button is toggled
  useEffect(() => {
    if (isManager) {
      loadData()
    }
  }, [isManager, isEditOn, loadData])

  // Reload data when publish is successful
  useEffect(() => {
    if (!isEditOn) {
      return
    }

    if (editStatus.published === true) {
      loadData()
    }
  }, [editStatus.published, isEditOn, loadData])

  /**
   * Reload pages and items whenever the page is visited
   */
  useEffect(() => {
    if (location.pathname.startsWith('/tipstools')) {
      loadData()
    }
  }, [location.pathname, loadData])

  /**
   * Add a new tips & tools item
   *
   * @param {[*]} items - An array of all current items
   * @param {number} order - Order of item above which the new item to be added
   */
  const handleAddRow = async (items, index) => {
    let newRows = Array.from(items)
    const newRowOrder = index + 1

    // Add a new row with dummy data below the item at `order` position
    let newRow = generateDummyRow()

    try {
      // Create a new draft post
      const createResponse = await createTipsToolsItem({
        variables: {
          ...newRow,
          clientMutationId: uuidv4(),
          order: newRowOrder,
          status: 'DRAFT',
        },
      })

      // New post created. Now, save the databaseId
      const newPostDatabaseId =
        createResponse?.data?.createTipsToolsItem?.tipsToolsItem?.databaseId

      // Add this `databaseId` to the `newRow`
      newRow = {
        ...newRow,
        databaseId: newPostDatabaseId,
        order: newRowOrder,
      }

      // Insert this new row into the list
      newRows.splice(newRowOrder, 0, newRow)

      // Since tips & tools item is an individual post, inserting a new one in
      // between requires a shift in all other posts' order.
      const newChanges = newRows.map((row, rowIndex) => {
        return {
          page: 'TIPS_TOOLS_ITEM',
          type: 'order',
          id: row.postId,
          databaseId: row.databaseId,
          content: rowIndex,
          item: row,
          action: 'update',
        }
      })

      let updatedChanges = [...getEditStatus().changes]
      for (let i = 0; i < newChanges.length; i++) {
        // const editStatusChange = getEditStatus().changes[i]
        const foundIndex = updatedChanges.findIndex(
          (change) => change.id === newChanges[i].id
        )

        if (foundIndex > -1) {
          updatedChanges[foundIndex] = { ...newChanges[i] }
        } else {
          updatedChanges.push({ ...newChanges[i] })
        }
      }

      setEditStatus({
        ...getEditStatus(),
        changes: updatedChanges,
      })

      // Save the updated modules
      setUpdatedItems(newRows)
    } catch (err) {
      toast.error(`Unable to add a new item at the moment. Please try again!`)
      console.error(`Unable to add a new item: ${err}`)
    }
  }

  /**
   * Remove a row at `index`
   */
  const handleRemoveRow = () => {
    if (removingDatabaseId === '') {
      toast.error(`Unable to remove this item: Unknown item.`)
      return
    }

    // Clone the modules array
    const newRows = items.filter(
      (item) => item.databaseId !== removingDatabaseId
    )

    const removingItem = items.find(
      (item) => item.databaseId === removingDatabaseId
    )

    // Add this to the editStatus
    const newChange = {
      databaseId: removingDatabaseId,
      page: 'TIPS_TOOLS_ITEM',
      id: removingItem?.postId,
      action: 'remove',
    }

    // Since tips & tools item is an individual post, deleting a new one in
    // between requires a shift in all other posts' order.
    let newChanges = newRows.map((row, rowIndex) => {
      return {
        page: 'TIPS_TOOLS_ITEM',
        type: 'order',
        id: row.postId,
        databaseId: row.databaseId,
        content: rowIndex,
        item: row,
        action: 'update',
      }
    })

    let updatedChanges = [...getEditStatus().changes]
    for (let i = 0; i < newChanges.length; i++) {
      const foundIndex = updatedChanges.findIndex(
        (change) => change.id === newChanges[i].id
      )

      if (foundIndex > -1) {
        updatedChanges[foundIndex] = { ...newChanges[i] }
      } else {
        updatedChanges.push({ ...newChanges[i] })
      }
    }

    // Also need filter out any `update` action related to the removed item
    updatedChanges = updatedChanges.filter(
      (change) => change.id !== newChange.id || change.type === 'remove'
    )

    setEditStatus({
      ...getEditStatus(),
      changes: [...updatedChanges, newChange],
    })

    // Save the updated modules
    setUpdatedItems(newRows)

    // Close the confirm dialog
    setRemovingDatabaseId(-1)
  }

  const handleReorderEditorCancel = () => {
    const { reorder, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  const handleReorderEditorConfirm = (updatedRowData = []) => {
    // Save the changes for publishing
    const updatedRows = updatedRowData.map((row, rowIndex) => {
      return { ...row, order: rowIndex }
    })

    // Create the new change set
    const newChanges = updatedRowData
      .map((row, rowIndex) => {
        return {
          page: 'TIPS_TOOLS_ITEM',
          type: 'order',
          databaseId: row.databaseId,
          content: rowIndex,
          action: 'update',
          item: row,
          // Mark this row as changed or unchanged
          changed: row.order !== rowIndex,
        }
      })
      .filter((row) => row.changed)

    let updatedChanges = Array.from(getEditStatus().changes)
    newChanges.forEach((newChange) => {
      const index = getEditStatus().changes.findIndex(
        (change) =>
          change.page === newChange.page &&
          change.type === newChange.type &&
          change.databaseId === newChange.databaseId
      )

      if (index > -1) {
        updatedChanges[index] = newChange
      } else {
        updatedChanges.push(newChange)
      }
    })

    setEditStatus({ ...getEditStatus(), changes: updatedChanges })

    setUpdatedItems(updatedRows)

    // Close the dialog
    handleReorderEditorCancel()
  }

  const handleOpenReorderEditor = () => {
    // In case of tips & tools, there's no need to apply the updated data to
    // updatedRows because the updates were already handled by other actions.

    // Open up the editor dialog
    setSearchParams({ ...serializeFromQuery(searchParams), reorder: 'true' })
  }

  const handleOpenEditor = (postId = '') => {
    // Open up the editor dialog
    setSearchParams({
      ...serializeFromQuery(searchParams),
      editor: 'true',
      pid: postId,
    })
  }

  const handleEditorConfirm = (updatedRowData = {}) => {
    console.log('handleEditorConfirm :: updatedRowData', updatedRowData)
    let updatingItem = items.find(
      (item) => item.postId === updatedRowData.postId
    )

    if (!updatingItem) {
      toast.warning(
        `An error has occurred which prevents your changes from saving improperly.`
      )
      return
    }

    const newItems = items.map((item) => {
      if (item.postId === updatedRowData.postId) {
        return updatedRowData
      } else {
        return item
      }
    })

    // Save the changes for publishing
    const newChange = {
      page: 'TIPS_TOOLS_ITEM',
      type: 'post',
      databaseId: updatedRowData.databaseId,
      content: updatedRowData,
      action: 'update',
    }

    let updatedChanges = Array.from(getEditStatus().changes)
    const index = getEditStatus().changes.findIndex(
      (change) =>
        change.page === newChange.page &&
        change.type === newChange.type &&
        change.databaseId === newChange.databaseId
    )

    if (index > -1) {
      updatedChanges[index] = newChange
    } else {
      updatedChanges.push(newChange)
    }

    setEditStatus({ ...getEditStatus(), changes: updatedChanges })

    setUpdatedItems(newItems)

    // Close the dialog
    handleEditorCancel()
  }

  const handleEditorCancel = () => {
    const { editor, pid, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  return (
    <main className="min-h-[calc(100vh-200px)] overflow-hidden">
      <div className="container mb-[120px] max-w-screen-content pt-14 sm:px-0">
        <div className="relative pb-[10px]">
          <div className="absolute -right-[150px] top-0 -z-[1] h-[395px] w-[217px] bg-none bg-contain bg-right-top bg-no-repeat opacity-20 sm:bg-tipstools-background lg:-right-[140px]"></div>
          {isDataLoading ? (
            <LoadingSkeleton />
          ) : !tipsToolsPage ? (
            <p className="text-center">No data</p>
          ) : (
            <>
              {isEditOn && (
                <>
                  {/** Task in progress dialog */}
                  <ConfirmDialog
                    yesText=""
                    noText=""
                    open={creatingTipsToolsItem}
                    text="Adding a new item..."
                  />
                  {/** Remove item dialog */}
                  <ConfirmDialog
                    open={removingDatabaseId !== -1}
                    title="Remove Tips & Tools Item"
                    text="Are you sure you want to remove this item?"
                    onCancel={() => setRemovingDatabaseId(-1)}
                    onConfirm={handleRemoveRow}
                  />
                  {/** Reorder dialog */}
                  <DialogReorderTipsToolsItems
                    open={searchParams.get('reorder') === 'true'}
                    rowData={items ?? []}
                    setRowData={setUpdatedItems}
                    title="Reorder Tips & Tools Items"
                    yesText="Save"
                    noText="Cancel"
                    onConfirm={handleReorderEditorConfirm}
                    onCancel={handleReorderEditorCancel}
                  />
                  {/** Edit dialog */}
                  <DialogEditTipsToolsItem
                    open={
                      searchParams.get('editor') === 'true' &&
                      searchParams.get('id') !== ''
                    }
                    rowData={items ?? []}
                    setRowData={setUpdatedItems}
                    title="Tips & Tools Item Editor"
                    yesText="Save"
                    noText="Cancel"
                    onConfirm={handleEditorConfirm}
                    onCancel={handleEditorCancel}
                  />
                </>
              )}
              {/** Title */}
              <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    doc={tipsToolsPage?.title ?? '<div></div>'}
                    toolbarOption={{
                      bold: false,
                      listNumber: false,
                      listBullet: false,
                    }}
                    tag={{
                      page: 'TIPS_TOOLS_PAGE',
                      type: 'title',
                      databaseId: tipsToolsPage.databaseId,
                      action: 'update',
                    }}
                    disableParagraph={true}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: tipsToolsPage?.title ?? '<div></div>',
                    }}
                  />
                )}
              </h1>
              {/** Overview */}
              {isEditOn ? (
                <EditableComponent
                  editable={isEditOn}
                  doc={tipsToolsPage?.overview ?? '<div></div>'}
                  tag={{
                    page: 'TIPS_TOOLS_PAGE',
                    type: 'overview',
                    databaseId: tipsToolsPage.databaseId,
                    action: 'update',
                  }}
                />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: tipsToolsPage?.overview ?? '<div></div>',
                  }}
                />
              )}
              <ul className="nostyle mt-10 [&>li:not(:last-of-type)]:mb-6">
                {items.map((item, index) => (
                  <li key={`${item.postId}${item.title}`} className="relative">
                    {isEditOn ? (
                      <button
                        className="flex w-full cursor-pointer select-none rounded-[5.5px] bg-bright-gray px-4 py-3 text-xs text-black-olive shadow-sm outline outline-0 hover:bg-beer/25 hover:outline-1 hover:outline-beer active:translate-y-[0.75px] active:scale-[0.99]"
                        onClick={() => handleOpenEditor(item.postId)}
                      >
                        <div className="flex flex-1 justify-between">
                          <div
                            dangerouslySetInnerHTML={{
                              __html: item?.title ?? '<div></div>',
                            }}
                          />
                          <ArrowRightIcon className="w-[18px]" />
                        </div>
                      </button>
                    ) : (
                      <div
                        className={clsx(
                          'flex w-full rounded-[5.5px] bg-bright-gray px-4 py-3 text-xs text-black-olive shadow-sm active:translate-y-[0.75px] active:scale-[0.99]'
                        )}
                      >
                        <Link
                          to={generateSafeLink(item.postId)}
                          className="flex flex-1 justify-between"
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: item?.title ?? '<div></div>',
                            }}
                          />
                          <ArrowRightIcon className="w-[18px]" />
                        </Link>
                      </div>
                    )}
                    {isEditOn && (
                      <>
                        <div className="absolute -right-[calc(5*12px)] top-1/2 flex -translate-y-1/2 justify-between space-x-1">
                          <button onClick={() => handleAddRow(items, index)}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={3}
                              stroke="currentColor"
                              className="h-[14px] w-[14px] stroke-green"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4.5v15m7.5-7.5h-15"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() =>
                              setRemovingDatabaseId(item.databaseId)
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="h-[14px] w-[14px] stroke-red-500"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                              />
                            </svg>
                          </button>
                          <button onClick={handleOpenReorderEditor}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              className="h-[14px] w-[14px]"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5"
                              />
                            </svg>
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </main>
  )
}

const generateDummyRow = () => {
  const dummyRow = {
    postId: uuidv4(),
    title: '<div>New tips & tools item</div>',
    detail: {
      resources: [],
    },
  }

  return dummyRow
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse space-y-2.5">
      {/** Title */}
      <div className="mb-10 flex w-full items-center justify-center space-x-2">
        <div className="mb-[28px] mt-[28px] h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Overview */}
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[400px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[440px] items-center space-x-2">
        <div className="h-2.5 w-32 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
      <div className="flex w-full max-w-[400] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="mb-10 flex w-full max-w-[360px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      {/** Tips & tools items */}
      <div className="!mt-10 flex w-full items-center space-x-2">
        <div className="w-full rounded-[5.5px] bg-gray-300 py-6 dark:bg-gray-600"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default TipsTools
